import { useCallback, useEffect, useState } from "react";
import { CTeDto, CTeFiltrarDto, STORAGECTE_KEY, getLocal, updateLocal } from "./CTe.Dto";
import useFilterManager from "src/hook/useFilterManager";
import { MapearErrosServidor, parseDate } from "src/hook/Functions";
import {
  CTeBuscarXML,
  CTeConsultarSituacao,
  CTeEnviar,
  CTeExcluir,
  CTeFiltrar,
  CTeImprimir,
} from "src/services/Fiscal.Service";
import { showToast } from "src/componentes";
import { useNavigate } from "react-router-dom";
import CTeMenuCode from "./CTe.Menu.Code";

const CTeCode = () => {
  const [data, setData] = useState<CTeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalShowPropriedades, setModalShowPropriedades] = useState(false);
  const [modalNovoShow, setModalNovoShow] = useState(false);
  const [cte, setCTe] = useState<CTeDto>();
  const navigate = useNavigate();

  const handleNovoCTe = useCallback(() => {
    const cteSalvo = getLocal<CTeDto>(STORAGECTE_KEY);
    if (cteSalvo && Object.keys(cteSalvo).length > 0) {
      setModalNovoShow(true);
    } else {
      navigate("/fiscal/cte/cadastro");
    }
  }, [navigate]);

  const menu = CTeMenuCode({
    setLoading,
    data,
    onNovoCTe: handleNovoCTe,
  });

  const useFilter = useFilterManager<CTeFiltrarDto>({
    codigoStatusEnvio: null,
    dataEmissaoInicial: null,
    dataEmissaoFinal: null,
    serie: 0,
  });

  useEffect(() => {
    const controller = new AbortController();

    const loadData = async () => {
      setLoading(true);
      try {
        await filtrar(useFilter.filters);
      } finally {
        setLoading(false);
      }
    };

    const hasValidDateRange =
      (useFilter.filters.dataEmissaoInicial &&
        useFilter.filters.dataEmissaoFinal) ||
      (!useFilter.filters.dataEmissaoInicial &&
        !useFilter.filters.dataEmissaoFinal);

    if (hasValidDateRange) {
      loadData();
    }

    return () => controller.abort();
  }, [useFilter.filters]);

  useEffect(() => {
    const newFilters: CTeFiltrarDto = {
      codigoStatusEnvio:
        useFilter.searchParams.get("codigoStatusEnvio") || null,
      dataEmissaoInicial: parseDate(
        useFilter.searchParams.get("dataEmissaoInicial")
      ),
      dataEmissaoFinal: parseDate(
        useFilter.searchParams.get("dataEmissaoFinal")
      ),
      serie: Number(useFilter.searchParams.get("serie")) || 0,
    };

    useFilter.setFilters((prev) =>
      JSON.stringify(prev) === JSON.stringify(newFilters) ? prev : newFilters
    );
  }, [useFilter.searchParams, useFilter.setFilters]);

  const filtrar = async (input: CTeFiltrarDto) => {
    try {
      const response = await CTeFiltrar(input);

      if (response.data.success) {
        setData(response.data.data);
      } else {
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao filtrar CT-e:", error);
      showToast({
        message: "Ocorreu erro ao buscar dados",
        title: "Erro",
        variant: "Danger",
      });
    }
  };

  const handleSelectStatus = (eventKey?: string | null) => {
    useFilter.updateFilter(
      "codigoStatusEnvio",
      eventKey === "99" ? null : eventKey
    );
  };

  const handleChangeSerie = (event: React.ChangeEvent<HTMLInputElement>) => {
    useFilter.updateFilter("serie", event.target.value || null);
  };

  const handleSetDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    useFilter.setMultipleFilters({
      dataEmissaoInicial: start || null,
      dataEmissaoFinal: end || null,
    });
  };

  const formatDate = (date: any) =>
    date ? new Date(date).toLocaleDateString("pt-BR") : "Selecionar";

  const getDatePickerTitle = () => {
    const { dataEmissaoInicial, dataEmissaoFinal } = useFilter.filters;

    if (!dataEmissaoInicial || !dataEmissaoFinal) {
      return "Emissão";
    }

    return `${formatDate(dataEmissaoInicial)} - ${formatDate(
      dataEmissaoFinal
    )}`;
  };

  const onReturnModalPropriedades = () => {
    setModalShowPropriedades(false);
  };

  const onDeleteModalPropriedades = async (value: CTeDto) => {
    if (!value.id) return;
    setLoading(true);
    await CTeExcluir(value.id)
      .then((response) => {
        if (response.data.success === true) {
          filtrar(useFilter.filters);
        }
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        console.error(error);
        showToast({
          message: "Ocorreu erro ao excluir CT-e",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onFetchStatusModalPropriedades = async (value: CTeDto) => {
    if (!value.id) return;
    onReturnModalPropriedades();
    setLoading(true);
    await CTeConsultarSituacao(value.id)
      .then((response) => {
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        console.error(error);
        showToast({
          message: "Ocorreu erro ao consultar situação",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onSendModalPropriedades = async (value: CTeDto) => {
    if (!value.id) return;
    onReturnModalPropriedades();
    setLoading(true);
    await CTeEnviar(value.id)
      .then((response) => {
        if (response.data.success === true) {
          filtrar(useFilter.filters);
          updateLocal<CTeDto>(STORAGECTE_KEY, null);
        }
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        console.error(error);
        showToast({
          message: "Ocorreu erro ao enviar CT-e",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onDownloadXml = async (value: CTeDto) => {
    if (!value.id) return;
    setLoading(true);
    setModalShowPropriedades(false);

    try {
      const nomeArquivo = `CTe_${value.numeroCte}`;
      const response = await CTeBuscarXML([value.id], nomeArquivo);
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${nomeArquivo}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      showToast({
        message: "Ocorreu erro ao baixar XML",
        title: "Erro",
        variant: "Danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const onPrintModalPropriedadesImprimir = async (value: CTeDto) => {
    if (!value.id) return;
    onReturnModalPropriedades();

    setLoading(true);
    await CTeImprimir(value.id)
      .then((response) => {
        if (response.data instanceof Blob) {
          const pdfBlob = new Blob([response.data], {
            type: "application/pdf",
          });
          const pdfUrl = URL.createObjectURL(pdfBlob);
          const isMobile = /Android|iPhone/i.test(navigator.userAgent);

          if (isMobile) {
            window.open(pdfUrl, "_blank");
          } else {
            const iframe = document.createElement("iframe");
            iframe.style.position = "fixed";
            iframe.style.right = "0";
            iframe.style.bottom = "0";
            iframe.style.width = "0";
            iframe.style.height = "0";
            iframe.src = pdfUrl;

            document.body.appendChild(iframe);

            iframe.onload = () => {
              iframe.contentWindow?.focus();
              iframe.contentWindow?.print();
            };
          }
        } else {
          MapearErrosServidor(response.data.notifications, showToast);
          return;
        }
      })
      .catch((error) => {
        console.error("Erro ao imprimir DACTE:", error);
        showToast({
          message: "Ocorreu erro ao imprimir DACTE",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onModalPropriedadesDownloadDacte = async (value: CTeDto) => {
    if (!value.id) return;
    onReturnModalPropriedades();

    setLoading(true);
    await CTeImprimir(value.id)
      .then((response) => {
        if (response.data instanceof Blob) {
          const pdfBlob = new Blob([response.data], {
            type: "application/pdf",
          });
          const pdfUrl = URL.createObjectURL(pdfBlob);
          const link = document.createElement("a");
          link.href = pdfUrl;
          link.download = `DacteCTe${value.numeroCte}.pdf`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(pdfUrl);
        } else {
          MapearErrosServidor(response.data.notifications, showToast);
          return;
        }
      })
      .catch((error) => {
        console.error("Erro ao baixar DACTE:", error);
        showToast({
          message: "Ocorreu erro ao baixar DACTE",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onModalPropriedadesShareDacte = async (value: CTeDto) => {
    if (!value.id) return;
    onReturnModalPropriedades();

    setLoading(true);
    await CTeImprimir(value.id)
      .then(async (response) => {
        if (response.data instanceof Blob) {
          const pdfBlob = new Blob([response.data], {
            type: "application/pdf",
          });
          try {
            const file = new File([pdfBlob], `DacteCTe${value.numeroCte}.pdf`, {
              type: "application/pdf",
            });

            if (navigator.canShare?.({ files: [file] })) {
              await navigator.share({
                title: "DACTE CT-e",
                files: [file],
              });
            } else {
              onModalPropriedadesDownloadDacte(value);
            }
          } catch (err) {
            console.error("Erro ao compartilhar:", err);
          }
        } else {
          MapearErrosServidor(response.data.notifications, showToast);
          return;
        }
      })
      .catch((error) => {
        console.error("Erro ao compartilhar DACTE:", error);
        showToast({
          message: "Ocorreu erro ao compartilhar DACTE",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const handleClickPropriedades = (value: CTeDto) => () => {
    setModalShowPropriedades(true);
    setCTe(value);
  };

  const onEditModalPropriedades = (value: CTeDto) => {
    setModalShowPropriedades(false);
    updateLocal<CTeDto>(STORAGECTE_KEY, value);
    navigate("/fiscal/cte/cadastro");
  };

  const onDuplicateModalPropriedades = (value: CTeDto) => {
    setModalShowPropriedades(false);
    const duplicated: CTeDto = {
      ...value,
      id: 0,
      numeroCte: 0,
    };
    updateLocal<CTeDto>(STORAGECTE_KEY, duplicated);
    navigate("/fiscal/cte/cadastro");
  };

  const onContinuarModalNovo = () => {
    setModalNovoShow(false);
    navigate("/fiscal/cte/cadastro");
  };

  const onCriarNovoModalNovo = () => {
    setModalNovoShow(false);
    updateLocal<CTeDto>(STORAGECTE_KEY, null);
    navigate("/fiscal/cte/cadastro");
  };

  const onCancelModalNovo = () => {
    setModalNovoShow(false);
  };

  return {
    data,
    loading,
    handleChangeSerie,
    handleSelectStatus,
    handleSetDates,
    useFilter,
    getDatePickerTitle,
    modalShowPropriedades,
    cte,
    onReturnModalPropriedades,
    onDeleteModalPropriedades,
    onFetchStatusModalPropriedades,
    onDownloadXml,
    onPrintModalPropriedadesImprimir,
    onModalPropriedadesDownloadDacte,
    onModalPropriedadesShareDacte,
    handleClickPropriedades,
    onSendModalPropriedades,
    onEditModalPropriedades,
    onDuplicateModalPropriedades,
    modalNovoShow,
    onContinuarModalNovo,
    onCriarNovoModalNovo,
    onCancelModalNovo,
    menu,
  };
};

export default CTeCode;
